import React, { useState } from "react";
import { FormControl, MenuItem, Select, ListSubheader, CircularProgress } from "@mui/material";
import { useAuth } from "../AuthProvider";

const BuildingDropdown = () => {
    const { user, switchOrganization, theme } = useAuth();
    const [loading, setLoading] = useState(false);

    // Identify user role / permissions
    const isSuperAdmin = user?.role === 'super_admin' || user?.permissions?.['subscriptions'];

    // Helper function to extract subscription name safely
    const getSubscriptionName = (org) => {
        return (
            org?.subscription_name || 
            org?.subscription?.name || 
            user?.subscription_name || 
            user?.subscription?.name || 
            "Subscription"
        );
    };

    // Extract organizations/buildings list
    const rawOrganizations = user?.userOrganizations?.length > 0
        ? user.userOrganizations
        : user?.organization_id
            ? [{
                organization_id: user.organization_id,
                organization_name: user.organization_name,
                subscription_name: user?.subscription_name || user?.subscription?.name
            }]
            : [];

    const currentOrgId = user?.organization_id;

    // Group organizations by subscription name
    const groupedSubscriptions = rawOrganizations.reduce((acc, org) => {
        const subName = getSubscriptionName(org);
        if (!acc[subName]) {
            acc[subName] = [];
        }
        acc[subName].push(org);
        return acc;
    }, {});

    const handleChange = async (event) => {
        const newOrgId = event.target.value;
        if (newOrgId === currentOrgId) return;

        setLoading(true);
        try {
            await switchOrganization(newOrgId);
        } catch (error) {
            console.error("Failed to switch organization:", error);
            alert("Could not switch organization. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormControl
            sx={{
                width: '20rem',
                height: '3rem',
                background: 'rgba(91,140,203,0.2)',
                borderRadius: '1rem',
                border: 'none',
            }}
        >
            <Select
                disabled={(rawOrganizations.length < 2 && !isSuperAdmin) || loading}
                value={currentOrgId || ""}
                onChange={handleChange}
                renderValue={(selected) => {
                    if (loading) return <CircularProgress size={20} />;
                    const active = rawOrganizations.find(opt => opt.organization_id === selected);
                    if (!active) return "Select Building";

                    const subName = getSubscriptionName(active);
                    return `${subName} / ${active.organization_name || 'Building'}`;
                }}
                sx={{
                    height: '3rem',
                    borderRadius: '1rem',
                    backgroundColor: theme === 'dark-theme' ? '' : 'rgba(91,140,203,0.2)',
                    color: theme === 'dark-theme' ? '#fff' : '#111827',
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                        backgroundColor: theme === 'dark-theme' ? '' : 'rgba(91,140,203,0.2)',
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                    '& .MuiSelect-select.Mui-disabled': {
                        color: theme === 'dark-theme' ? '#fff' : '#111827',
                        WebkitTextFillColor: theme === 'dark-theme' ? '#fff' : '#111827',
                    }
                }}
            >
                {Object.entries(groupedSubscriptions).map(([subscriptionName, orgs]) => [
                    <ListSubheader
                        key={`sub-header-${subscriptionName}`}
                        sx={{
                            fontWeight: 'bold',
                            backgroundColor: theme === 'dark-theme' ? '#1e293b' : '#f1f5f9',
                            color: theme === 'dark-theme' ? '#94a3b8' : '#475569',
                            lineHeight: '32px'
                        }}
                    >
                        {subscriptionName}
                    </ListSubheader>,
                    ...orgs.map((op) => {
                        const subName = getSubscriptionName(op);
                        return (
                            <MenuItem key={op.organization_id} value={op.organization_id} sx={{ pl: 4 }}>
                                {`${subName} / ${op.organization_name || `Building ${op.organization_id}`}`}
                            </MenuItem>
                        );
                    })
                ])}
            </Select>
        </FormControl>
    );
};

export default BuildingDropdown;